import React, { useState, useRef, useEffect } from 'react';
import { aiApiFetch } from '../utils/apiFetch';
import { jsPDF } from "jspdf";
import { marked } from "marked"; // For markdown to HTML conversion
import HybridTAGRenderer from '../components/HybridTAGRenderer';
import mammoth from 'mammoth';
import '../styles/TAGscreening.css';
import '../styles/TAGStructured.css'; // Import the new CSS
import { FiUpload, FiFileText, FiAlertTriangle, FiTrash2, FiSearch, FiDownload, FiFile, FiAward } from 'react-icons/fi';

// Import pdfjs but handle the worker differently
import * as pdfjs from 'pdfjs-dist/webpack';

function TAGscreening() {
  useEffect(() => {
    try {
      const workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
      pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
      console.log("PDF.js worker set up successfully");
    } catch (error) {
      console.error("Error setting up PDF.js worker:", error);
    }
  }, []);

  const [jdFile, setJdFile] = useState(null);
  const [jdContent, setJdContent] = useState('');
  const [showJdTooltip, setShowJdTooltip] = useState(false);
  const [assessmentResult, setAssessmentResult] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // Track current progress step
  const jdInputRef = useRef(null);
  
  // Supported file types (expanded from original to include PDF)
  const validFileTypes = [
    'text/plain', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/pdf'
  ];
  
  // File extensions for display purposes
  const supportedFormats = '.txt, .docx, .pdf';

  // Function to extract text from PDF file
  const extractTextFromPDF = async (file) => {
    try {
      // Convert file to ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();
      // Get PDF document
      const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      // Extract text from each page
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n';
      }
      
      return fullText;
    } catch (err) {
      console.error('Error extracting text from PDF:', err);
      throw new Error('Failed to extract text from PDF file');
    }
  };

  // Enhanced file handler for JD
  const handleJdFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage(`Please upload a file in ${supportedFormats} format.`);
      setJdFile(null);
      setJdContent('');
      return;
    }

    setErrorMessage('');
    setJdFile(file);

    try {
      let content = '';
      // Process based on file type
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        // DOCX file
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        content = result.value;
      } else if (file.type === 'application/pdf') {
        // PDF file
        content = await extractTextFromPDF(file);
      } else {
        // TXT file or other text-based formats
        content = await readFileAsText(file);
      }
      
      setJdContent(content);
      setCurrentStep(2); // Move to step 2 after file upload
    } catch (err) {
      console.error("Error processing file:", err);
      setErrorMessage(`Failed to process job description file: ${err.message}`);
    }
  };

  const readFileAsText = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = (e) => reject(e);
      reader.readAsText(file);
    });
  };

  // Drag and drop handler
  const handleDragOver = (e) => {
    e.preventDefault();
    e.currentTarget.classList.add('dragging');
  };

  const handleDragLeave = (e) => {
    e.currentTarget.classList.remove('dragging');
  };

  // Drop handler with PDF support
  const handleDrop = async (e) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragging');

    const file = e.dataTransfer.files[0];
    if (!file) return;

    if (!validFileTypes.includes(file.type)) {
      setErrorMessage(`Please upload a job description in ${supportedFormats} format.`);
      return;
    }

    setErrorMessage('');
    setJdFile(file);
    
    // Create a DataTransfer object to set files property
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    jdInputRef.current.files = dataTransfer.files;

    try {
      let content = '';
      if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() });
        content = result.value;
      } else if (file.type === 'application/pdf') {
        content = await extractTextFromPDF(file);
      } else {
        content = await readFileAsText(file);
      }
      
      setJdContent(content);
      setCurrentStep(2); // Move to step 2 after file upload
    } catch (err) {
      console.error("Error processing file:", err);
      setErrorMessage(`Failed to process job description file: ${err.message}`);
    }
  };

  // Updated to handle both structured and markdown responses
  const handleAssessClick = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setError(null);
    
    if (isLoading) return; // Prevent multiple submissions
    
    try {
      const response = await aiApiFetch('/ragscreening', {
        method: 'POST',
        body: JSON.stringify({ jobDescriptionText: jdContent }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`HTTP error! status: ${response.status}, message: ${errorData.error || response.statusText}`);
      }

      const data = await response.json();
      if (data.result && typeof data.result === 'object' && data.result.error) {
        throw new Error(data.result.error);
      }
      
      // Store the result directly - HybridTAGRenderer will handle parsing
      setAssessmentResult(data.result);
      setCurrentStep(3); // Move to step 3 after generating questions
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setJdFile(null);
    setJdContent('');
    setErrorMessage('');
    setAssessmentResult(null);
    setCurrentStep(1); // Reset to step 1

    // Reset file input
    if (jdInputRef.current) {
      jdInputRef.current.value = null;
    }
  };

  const removeFile = () => {
    setJdFile(null);
    setJdContent('');
    if (jdInputRef.current) jdInputRef.current.value = null;
    setCurrentStep(1); // Reset to step 1
  };

  // Function to decode HTML entities
  function decodeHtmlEntities(text) {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.documentElement.textContent || doc.documentElement.innerText;
  }
  
  // Enhanced download function for both structured and markdown content
  const downloadAssessmentResult = () => {
  // Create a PDF document with proper margins
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });
  
  // Define page dimensions and margins
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margins = { 
    top: 15, 
    left: 15,  // Increased left margin
    right: 15,
    bottom: 15 
  };
  
  // Calculate usable width for text
  const textAreaWidth = pageWidth - margins.left - margins.right;
  
  let content = '';
  let structured = null;
  
  // Parse the result if it's a structured JSON response
  try {
    const parsedResult = typeof assessmentResult === 'string' 
      ? JSON.parse(assessmentResult)
      : assessmentResult;
      
    if (parsedResult && parsedResult.structured) {
      structured = parsedResult.structured;
      content = parsedResult.markdown || '';
    } else if (parsedResult && parsedResult.markdown) {
      content = parsedResult.markdown;
    } else {
      // Fallback to treating as plain markdown
      content = typeof assessmentResult === 'string' ? assessmentResult : '';
    }
  } catch (e) {
    // If parsing fails, use the result as-is
    content = typeof assessmentResult === 'string' ? assessmentResult : '';
  }

  // Convert the Markdown content to HTML if needed
  let htmlContent = content ? marked(content) : '';
  
  // Decode HTML entities
  htmlContent = decodeHtmlEntities(htmlContent);

  // Set title and add header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text("Technical Assessment Guide (TAG) Questions", margins.left, margins.top);
  
  // Draw a line under the title
  doc.setLineWidth(0.5);
  doc.line(margins.left, margins.top + 3, pageWidth - margins.right, margins.top + 3);
  
  // Add generation info
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100); // Gray color
  const today = new Date();
  doc.text(`Generated: ${today.toLocaleDateString()} ${today.toLocaleTimeString()}`, margins.left, margins.top + 8);
  
  // Reset text color
  doc.setTextColor(0, 0, 0); // Black
  
  // Set font size for the content
  const contentFontSize = 11;
  doc.setFontSize(contentFontSize);
  
  // Define bold phrases to be applied in the PDF
  const boldPhrases = [
    "Technical Interview Questions:",
    "High Priority Questions:",
    "Medium Priority Questions:",
    "Low Priority Questions:",
    "Behavioral Interview Questions:",
    "Evaluation Guidance:",
    "Question:",
    "Ideal Answer:",
    "Assessment:"
  ];

  // Start position below the header
  let yPosition = margins.top + 15;
  
  // If we have structured data, create a more organized PDF
  if (structured) {
    // Group technical questions by priority
    const highPriority = structured.technicalQuestions?.filter(q => q.priority === "High") || [];
    const mediumPriority = structured.technicalQuestions?.filter(q => q.priority === "Medium") || [];
    const lowPriority = structured.technicalQuestions?.filter(q => q.priority === "Low") || [];
    const unspecifiedPriority = structured.technicalQuestions?.filter(q => !q.priority) || [];
    
    // If we don't have prioritized questions, distribute them
    if (highPriority.length === 0 && mediumPriority.length === 0 && lowPriority.length === 0 && unspecifiedPriority.length > 0) {
      const totalQuestions = unspecifiedPriority.length;
      const highCount = Math.ceil(totalQuestions * 0.4);
      const mediumCount = Math.ceil(totalQuestions * 0.3);
      
      for (let i = 0; i < unspecifiedPriority.length; i++) {
        if (i < highCount) {
          highPriority.push({...unspecifiedPriority[i], priority: "High"});
        } else if (i < highCount + mediumCount) {
          mediumPriority.push({...unspecifiedPriority[i], priority: "Medium"});
        } else {
          lowPriority.push({...unspecifiedPriority[i], priority: "Low"});
        }
      }
    }
    
    // Add Technical Questions Section Header
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text("TECHNICAL INTERVIEW QUESTIONS", margins.left, yPosition);
    yPosition += 8;
    
    // Add High Priority Questions
    if (highPriority.length > 0) {
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(187, 30, 30); // Dark red
      doc.text("High Priority Questions", margins.left, yPosition);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPosition += 6;
      
      for (let i = 0; i < highPriority.length; i++) {
        const question = highPriority[i];
        
        // Check if we need a new page
        if (yPosition > pageHeight - margins.bottom - 30) {
          doc.addPage();
          yPosition = margins.top;
        }
        
        // Add question number and text
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Q${i+1}: ${question.question}`, margins.left, yPosition);
        yPosition += 6;
        
        // Add explanation/assessment
        if (question.explanation) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(10);
          const explanationLines = doc.splitTextToSize(`Purpose: ${question.explanation}`, textAreaWidth);
          for (let j = 0; j < explanationLines.length; j++) {
            doc.text(explanationLines[j], margins.left, yPosition);
            yPosition += 5;
          }
        }
        
        // Add answer if available
        if (question.answer) {
          yPosition += 1;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text("Ideal Answer:", margins.left, yPosition);
          yPosition += 5;
          
          const answerLines = doc.splitTextToSize(question.answer, textAreaWidth - 5);
          for (let j = 0; j < answerLines.length; j++) {
            doc.text(answerLines[j], margins.left + 5, yPosition);
            yPosition += 5;
          }
        }
        
        yPosition += 5; // Add space between questions
      }
    }
    
    // Add Medium Priority Questions
    if (mediumPriority.length > 0) {
      // Check if we need a new page
      if (yPosition > pageHeight - margins.bottom - 30) {
        doc.addPage();
        yPosition = margins.top;
      }
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(214, 110, 41); // Orange
      doc.text("Medium Priority Questions", margins.left, yPosition);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPosition += 6;
      
      for (let i = 0; i < mediumPriority.length; i++) {
        const question = mediumPriority[i];
        
        // Check if we need a new page
        if (yPosition > pageHeight - margins.bottom - 30) {
          doc.addPage();
          yPosition = margins.top;
        }
        
        // Add question number and text
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Q${i+1}: ${question.question}`, margins.left, yPosition);
        yPosition += 6;
        
        // Add explanation/assessment
        if (question.explanation) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(10);
          const explanationLines = doc.splitTextToSize(`Purpose: ${question.explanation}`, textAreaWidth);
          for (let j = 0; j < explanationLines.length; j++) {
            doc.text(explanationLines[j], margins.left, yPosition);
            yPosition += 5;
          }
        }
        
        // Add answer if available
        if (question.answer) {
          yPosition += 1;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text("Ideal Answer:", margins.left, yPosition);
          yPosition += 5;
          
          const answerLines = doc.splitTextToSize(question.answer, textAreaWidth - 5);
          for (let j = 0; j < answerLines.length; j++) {
            doc.text(answerLines[j], margins.left + 5, yPosition);
            yPosition += 5;
          }
        }
        
        yPosition += 5; // Add space between questions
      }
    }
    
    // Add Low Priority Questions
    if (lowPriority.length > 0) {
      // Check if we need a new page
      if (yPosition > pageHeight - margins.bottom - 30) {
        doc.addPage();
        yPosition = margins.top;
      }
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(46, 125, 50); // Green
      doc.text("Low Priority Questions", margins.left, yPosition);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPosition += 6;
      
      for (let i = 0; i < lowPriority.length; i++) {
        const question = lowPriority[i];
        
        // Check if we need a new page
        if (yPosition > pageHeight - margins.bottom - 30) {
          doc.addPage();
          yPosition = margins.top;
        }
        
        // Add question number and text
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Q${i+1}: ${question.question}`, margins.left, yPosition);
        yPosition += 6;
        
        // Add explanation/assessment
        if (question.explanation) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(10);
          const explanationLines = doc.splitTextToSize(`Purpose: ${question.explanation}`, textAreaWidth);
          for (let j = 0; j < explanationLines.length; j++) {
            doc.text(explanationLines[j], margins.left, yPosition);
            yPosition += 5;
          }
        }
        
        // Add answer if available
        if (question.answer) {
          yPosition += 1;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text("Ideal Answer:", margins.left, yPosition);
          yPosition += 5;
          
          const answerLines = doc.splitTextToSize(question.answer, textAreaWidth - 5);
          for (let j = 0; j < answerLines.length; j++) {
            doc.text(answerLines[j], margins.left + 5, yPosition);
            yPosition += 5;
          }
        }
        
        yPosition += 5; // Add space between questions
      }
    }
    
    // Add Behavioral Questions - start on a new page
    if (structured.behavioralQuestions?.length > 0) {
      doc.addPage();
      yPosition = margins.top;
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(100, 40, 160); // Purple
      doc.text("BEHAVIORAL INTERVIEW QUESTIONS", margins.left, yPosition);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPosition += 8;
      
      for (let i = 0; i < structured.behavioralQuestions.length; i++) {
        const question = structured.behavioralQuestions[i];
        
        // Check if we need a new page
        if (yPosition > pageHeight - margins.bottom - 30) {
          doc.addPage();
          yPosition = margins.top;
        }
        
        // Add question number and text
        doc.setFontSize(11);
        doc.setFont('helvetica', 'bold');
        doc.text(`Q${i+1}: ${question.question}`, margins.left, yPosition);
        yPosition += 6;
        
        // Add explanation/assessment
        if (question.explanation) {
          doc.setFont('helvetica', 'italic');
          doc.setFontSize(10);
          const explanationLines = doc.splitTextToSize(`Purpose: ${question.explanation}`, textAreaWidth);
          for (let j = 0; j < explanationLines.length; j++) {
            doc.text(explanationLines[j], margins.left, yPosition);
            yPosition += 5;
          }
        }
        
        // Add answer if available
        if (question.answer) {
          yPosition += 1;
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(10);
          doc.text("Ideal Answer:", margins.left, yPosition);
          yPosition += 5;
          
          const answerLines = doc.splitTextToSize(question.answer, textAreaWidth - 5);
          for (let j = 0; j < answerLines.length; j++) {
            doc.text(answerLines[j], margins.left + 5, yPosition);
            yPosition += 5;
          }
        }
        
        yPosition += 5; // Add space between questions
      }
    }
    
    // Add Evaluation Guidance if available
    if (structured.evaluationGuidance && structured.evaluationGuidance.length > 0) {
      // Start on a new page if needed
      if (yPosition > pageHeight - margins.bottom - 40) {
        doc.addPage();
        yPosition = margins.top;
      }
      
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0, 120, 60); // Green
      doc.text("EVALUATION GUIDANCE", margins.left, yPosition);
      doc.setTextColor(0, 0, 0); // Reset to black
      yPosition += 8;
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      
      if (Array.isArray(structured.evaluationGuidance)) {
        for (let i = 0; i < structured.evaluationGuidance.length; i++) {
          // Check if we need a new page
          if (yPosition > pageHeight - margins.bottom - 10) {
            doc.addPage();
            yPosition = margins.top;
          }
          
          const point = structured.evaluationGuidance[i];
          const pointLines = doc.splitTextToSize(`${i+1}. ${point}`, textAreaWidth);
          
          for (let j = 0; j < pointLines.length; j++) {
            doc.text(pointLines[j], margins.left, yPosition);
            yPosition += 5;
          }
          
          yPosition += 2; // Add spacing between points
        }
      } else if (typeof structured.evaluationGuidance === 'string') {
        const guidanceLines = doc.splitTextToSize(structured.evaluationGuidance, textAreaWidth);
        for (let i = 0; i < guidanceLines.length; i++) {
          doc.text(guidanceLines[i], margins.left, yPosition);
          yPosition += 5;
        }
      }
    }
  }
  // Fallback to markdown processing if no structured data is available
  else {
    // Split the content based on new lines and process line by line
    const textContent = htmlContent.split('\n');

    // Iterate through each line of content
    textContent.forEach(line => {
      let processedLine = line;
      let lineProcessed = false;

      // Check if the line should be bold (contains a bold phrase)
      for (let phrase of boldPhrases) {
        if (processedLine.includes(phrase)) {
          lineProcessed = true;
          
          // Check if we need a new page
          if (yPosition > pageHeight - margins.bottom - 15) {
            doc.addPage();
            yPosition = margins.top;
          }
          
          // Text before the bold part
          const beforeBold = processedLine.split(phrase)[0];
          const afterBold = processedLine.split(phrase)[1] || '';
          
          let currentX = margins.left;
          
          // Print before the bold part normally if it exists
          if (beforeBold) {
            doc.setFont('helvetica', 'normal');
            doc.text(beforeBold, currentX, yPosition);
            currentX += doc.getTextWidth(beforeBold);
          }

          // Print the bold part
          doc.setFont('helvetica', 'bold');
          doc.text(phrase, currentX, yPosition);
          currentX += doc.getTextWidth(phrase);

          // Print after the bold part if it exists
          if (afterBold) {
            doc.setFont('helvetica', 'normal');
            
            // Handle multi-line text after a bold phrase
            const afterLines = doc.splitTextToSize(afterBold, textAreaWidth - (currentX - margins.left));
            
            if (afterLines.length === 1) {
              // Single line - continue on the same line
              doc.text(afterBold, currentX, yPosition);
            } else {
              // Multi-line - start on the next line
              yPosition += 5;
              for (let i = 0; i < afterLines.length; i++) {
                doc.text(afterLines[i], margins.left, yPosition);
                yPosition += 5;
              }
            }
          }
          
          yPosition += 5; // Move to the next line
          break; // Exit the bold phrases loop once found
        }
      }

      // If the line doesn't contain any bold phrases, print it normally
      if (!lineProcessed && processedLine.trim().length > 0) {
        // Check if we need a new page
        if (yPosition > pageHeight - margins.bottom - 10) {
          doc.addPage();
          yPosition = margins.top;
        }
        
        doc.setFont('helvetica', 'normal');
        const plainLines = doc.splitTextToSize(processedLine, textAreaWidth);
        
        for (let i = 0; i < plainLines.length; i++) {
          doc.text(plainLines[i], margins.left, yPosition);
          yPosition += 5;
        }
      }
    });
  }

  // Save the generated PDF
  doc.save("tag_interview_questions.pdf");
};

  const isAssessButtonEnabled = jdContent;

  return (
    <div className="tag-screening-container">
      {/* Progress Tracker */}
      <div className="progress-tracker">
        <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
          <div className="step-indicator">
            <FiFile size={18} />
          </div>
          <div className="step-label">Upload JD</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
          <div className="step-indicator">
            <FiAward size={18} />
          </div>
          <div className="step-label">Generate Questions</div>
        </div>
        <div className="progress-connector"></div>

        <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
          <div className="step-indicator">
            <FiDownload size={18} />
          </div>
          <div className="step-label">Download PDF</div>
        </div>
      </div>
      
      {/* Error Message */}
      {errorMessage && (
        <div className="error-notification">
          <FiAlertTriangle size={20} />
          <span>{errorMessage}</span>
        </div>
      )}

      <div className="tag-screening-content">
        {/* Left column with upload section */}
        <div className="upload-section-container">
          <div className="upload-card">
            <div className="card-header">
              <h3>Job Description Upload</h3>
              <p className="card-description">
                Upload a job description to generate relevant technical interview questions and answers.
              </p>
            </div>
            <div className="card-body">
              <div className="upload-area">
                <h4 className="upload-area-title">
                  <FiFileText className="area-icon" />
                  Job Description File
                </h4>
                
                {!jdFile ? (
                  <div
                    className="file-drop-area"
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => jdInputRef.current.click()}
                  >
                    <div className="upload-icon">
                      <FiUpload size={28} color="#1976d2" />
                    </div>
                    <div className="upload-text">
                      <p className="upload-subtitle">
                        Drop your job description here, or <span className="browse-text">browse</span>
                      </p>
                      <p className="file-types">Supported formats: {supportedFormats}</p>
                    </div>
                    <input
                      ref={jdInputRef}
                      id="jd-upload"
                      type="file"
                      accept=".txt, .docx, .pdf"
                      onChange={handleJdFileChange}
                      className="file-input"
                    />
                  </div>
                ) : (
                  <div className="file-preview">
                    <div className="file-info">
                      <div className="file-icon">
                        <FiFileText size={24} color="#1976d2" />
                      </div>
                      <div className="file-details"
                        onMouseEnter={() => setShowJdTooltip(true)}
                        onMouseLeave={() => setShowJdTooltip(false)}
                      >
                        <p className="file-name">{jdFile.name}</p>
                        <p className="file-size">{(jdFile.size / 1024).toFixed(2)} KB</p>
                        
                        {/* Show tooltip on hover */}
                        {showJdTooltip && (
                          <div className="tooltip">
                            <p>{jdContent.length > 250 ? jdContent.slice(0, 250) + '...' : jdContent}</p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="file-actions">
                      <button
                        className="icon-button preview-btn"
                        title="Preview content"
                        onClick={() => alert(jdContent.substring(0, 500) + "...")}
                      >
                        <FiSearch size={18} />
                      </button>
                      <button
                        className="icon-button remove-btn"
                        title="Remove file"
                        onClick={removeFile}
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button
              className={`btn btn-primary ${!isAssessButtonEnabled ? 'disabled' : ''}`}
              onClick={handleAssessClick}
              disabled={!isAssessButtonEnabled || isLoading || currentStep > 2}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Generating Questions...
                </>
              ) : (
                <>Generate TAG Q&A</>
              )}
            </button>
            <button
              className="btn btn-secondary"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>
        
        {/* Right column with Q&A preview */}
        <div className="results-section">
          <div className="qa-preview-container">
            <h3 className="preview-title">Technical & Behaviour Q&A Guide</h3>
            
            {!assessmentResult ? (
              <div className="no-qa-message">
                <FiFileText size={32} color="#ccc" />
                <p>Upload a job description and generate Q&A to see results</p>
              </div>
            ) : (
              <div className="qa-preview">
                <p className="preview-description">
                  The TAG screening questions have been generated based on the job description.
                  You can download them as a PDF using the button below.
                </p>
                <div className="download-button-container">
                  <button 
                    className="btn btn-download"
                    onClick={downloadAssessmentResult}
                  >
                    <FiDownload size={18} />
                    Download Questions & Answers
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Assessment Results */}
      {(isLoading || assessmentResult || error) && (
        <div className="assessment-results-container">
          

          <div className="assessment-content">
            {isLoading ? (
              <div className="loader-container">
                <div className="loader"></div>
                <div className="loader-text">Generating technical and Behaviour questions and answers...</div>
              </div>
            ) : error ? (
              <div className="error-container">
                <FiAlertTriangle size={28} />
                <h3>Error</h3>
                <p>{error}</p>
              </div>
            ) : assessmentResult ? (
              <div className="questions-result">
                <HybridTAGRenderer evaluationResult={assessmentResult} />
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}

export default TAGscreening;